import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Receipt, 
  Download, 
  FileText, 
  FileSpreadsheet, 
  CheckCircle2, 
  Clock, 
  AlertCircle, 
  X, 
  ChevronRight, 
  Sparkles,
  Sliders,
  DollarSign,
  ArrowRight,
  ShieldCheck,
  Send,
  Shield,
  HelpCircle,
  TrendingUp,
  Percent,
  FileCheck
} from 'lucide-react';
import { Booking, Expense, ServiceType, BusinessSettings } from '../types';
import { getBusinessSettings, saveBusinessSettings } from '../utils/businessSettings';
import { downloadFile } from './ImportExportCenter';
import { getJurisdictionPreset } from '../utils/jurisdictionPresets';

interface FiscalYearTaxReminderProps {
  bookings: Booking[];
  expenses: Expense[];
  streams: ServiceType[];
  settings: BusinessSettings;
  onOpenExportsTab: () => void;
  showToast: (msg: string, type?: 'success' | 'info') => void;
}

const FISCAL_MONTH_OPTIONS = [
  { value: 1, label: 'January 31' },
  { value: 2, label: 'February 28/29 (South Africa, Kenya)' },
  { value: 3, label: 'March 31 (UK, India, Japan, Canada)' },
  { value: 4, label: 'April 30' },
  { value: 5, label: 'May 31' },
  { value: 6, label: 'June 30 (Australia, Egypt, NZ)' },
  { value: 7, label: 'July 31' },
  { value: 8, label: 'August 31' },
  { value: 9, label: 'September 30 (US Non-Profits / Corp)' },
  { value: 10, label: 'October 31' },
  { value: 11, label: 'November 30' },
  { value: 12, label: 'December 31 (US, EU, Standard Calendar)' }
];

function FiscalYearTaxReminderComponent({
  bookings,
  expenses,
  streams,
  settings,
  onOpenExportsTab,
  showToast
}: FiscalYearTaxReminderProps) {
  const [isDismissed, setIsDismissed] = useState(false);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'zero_tax' | 'standard'>('zero_tax');
  const [selectedMonth, setSelectedMonth] = useState<number>(settings.fiscalYearEndMonth || 2);
  const [downloading, setDownloading] = useState<string | null>(null);

  const STORAGE_KEY = 'azuraos_fiscal_tax_reminder_dismissed_until';

  useEffect(() => {
    try {
      const dismissedUntil = localStorage.getItem(STORAGE_KEY);
      if (dismissedUntil) {
        const timestamp = parseInt(dismissedUntil, 10);
        if (Date.now() < timestamp) {
          setIsDismissed(true);
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  // Calculate Fiscal Year End Proximity
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1; // 1-indexed (1 to 12)
  const currentDay = today.getDate();

  const targetMonth = selectedMonth || 2;
  
  // Calculate next target fiscal year end date
  let fiscalYear = currentYear;
  if (currentMonth > targetMonth || (currentMonth === targetMonth && currentDay > 25)) {
    fiscalYear = currentYear + 1;
  }

  // Get last day of target month
  const lastDayOfMonth = new Date(fiscalYear, targetMonth, 0).getDate();
  const fiscalEndDate = new Date(fiscalYear, targetMonth - 1, lastDayOfMonth);

  // Difference in days
  const diffTime = fiscalEndDate.getTime() - today.getTime();
  const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  const handleDismiss = (days: number = 14) => {
    const until = Date.now() + days * 24 * 60 * 60 * 1000;
    localStorage.setItem(STORAGE_KEY, until.toString());
    setIsDismissed(true);
    showToast(`Reminder snoozed for ${days} days.`);
  };

  const handleUpdateFiscalMonth = (newMonth: number) => {
    setSelectedMonth(newMonth);
    const updated = { ...settings, fiscalYearEndMonth: newMonth };
    saveBusinessSettings(updated);
    setIsConfigOpen(false);
    showToast(`Fiscal year-end updated to ${FISCAL_MONTH_OPTIONS.find(m => m.value === newMonth)?.label.split(' ')[0]}`, 'success');
  };

  // Financial and Tax Metrics
  const preset = getJurisdictionPreset(settings.jurisdiction);
  const rate = settings.vatRate !== undefined ? settings.vatRate : preset.vatRate;
  const divisor = 1 + rate / 100;
  const cur = settings.currencySymbol || preset.currencySymbol;
  const taxLabel = settings.taxAuthorityLabel || preset.taxAuthorityLabel;

  // Active bookings (excluding cancelled/void invoices)
  const activeBookings = bookings.filter(b => b.status !== 'cancelled');
  const paidBookings = bookings.filter(b => b.status === 'paid');
  const totalGrossRev = activeBookings.reduce((sum, b) => sum + (Number(b.price) || 0), 0);
  const paidGrossRev = paidBookings.reduce((sum, b) => sum + (Number(b.price) || 0), 0);
  const totalNetRev = totalGrossRev / divisor;
  const totalOutputVat = totalGrossRev - totalNetRev;

  const totalGrossExp = expenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
  const totalNetExp = totalGrossExp / divisor;
  const totalInputVat = totalGrossExp - totalNetExp;

  const netTaxPayable = totalOutputVat - totalInputVat;
  const netTaxableProfit = totalGrossRev - totalGrossExp;

  // Threshold Calculations
  const vatThreshold = preset.vatThresholdAmount || 1000000;
  const thresholdPercentage = Math.min(100, (totalGrossRev / vatThreshold) * 100);
  const remainingToThreshold = Math.max(0, vatThreshold - totalGrossRev);
  const isUnderThreshold = totalGrossRev < vatThreshold;
  const isZeroTaxEligible = isUnderThreshold && (netTaxableProfit <= (preset.taxFreeAllowanceAmount || 95750));

  // Generate & Print Nil Return (Zero Liability Return Pack)
  const handlePrintNilReturnPDF = () => {
    setDownloading('nil_pdf');
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Could not open print window. Please allow popups for AzuraOS.');
      setDownloading(null);
      return;
    }

    const dateStr = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    const formattedFiscalEnd = fiscalEndDate.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });

    printWindow.document.write(`
      <html>
        <head>
          <title>${preset.nilReturnTitle} — ${settings.companyName || 'AzuraOS Entity'}</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #0F172A; line-height: 1.5; padding: 35px; max-width: 850px; margin: 0 auto; background: #FFF; }
            .no-print { background: #ECFDF5; border: 1px solid #A7F3D0; padding: 12px 20px; border-radius: 8px; margin-bottom: 25px; display: flex; justify-content: space-between; align-items: center; font-size: 13px; color: #065F46; }
            .header { border-bottom: 3px solid #0F172A; padding-bottom: 15px; margin-bottom: 25px; display: flex; justify-content: space-between; align-items: flex-start; }
            .company { font-size: 22px; font-weight: 800; color: #0F172A; margin: 0; }
            .doc-title { font-size: 14px; font-weight: 700; text-transform: uppercase; color: #0D9488; letter-spacing: 0.05em; margin-top: 2px; }
            .meta { font-size: 11px; color: #64748B; text-align: right; }
            .badge-safe { background: #D1FAE5; color: #065F46; border: 1px solid #6EE7B7; padding: 3px 10px; border-radius: 6px; font-weight: 800; font-size: 11px; display: inline-block; }
            
            .box-guidance { background: #F8FAFC; border: 1.5px solid #E2E8F0; border-radius: 10px; padding: 16px; margin-bottom: 25px; font-size: 12px; }
            .kpi-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin-bottom: 25px; }
            .kpi-card { background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 10px; padding: 15px; }
            .kpi-card-zero { background: #064E3B; color: white; border-radius: 10px; padding: 15px; }
            .kpi-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #64748B; margin-bottom: 4px; }
            .kpi-card-zero .kpi-label { color: #6EE7B7; }
            .kpi-value { font-size: 22px; font-weight: 800; font-family: monospace; }
            .kpi-sub { font-size: 10px; margin-top: 4px; color: #64748B; }
            .kpi-card-zero .kpi-sub { color: #A7F3D0; }

            .section-header { font-size: 13px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.05em; color: #0F172A; border-bottom: 2px solid #E2E8F0; padding-bottom: 6px; margin: 25px 0 12px 0; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 15px; }
            th { background: #F8FAFC; text-align: left; padding: 8px 12px; font-weight: 700; color: #475569; border-bottom: 2px solid #E2E8F0; }
            td { padding: 8px 12px; border-bottom: 1px solid #F1F5F9; }

            .filing-table th { background: #0F172A; color: white; }
            .filing-table td { font-family: monospace; font-size: 12px; }
            
            .loss-box { background: #FEF3C7; border: 1px solid #FCD34D; border-radius: 8px; padding: 14px; margin-top: 20px; font-size: 11.5px; color: #92400E; }

            @media print {
              body { padding: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="no-print">
            <span>🛡️ <strong>Zero-Liability (Nil Return) Pack:</strong> Print or Save as PDF to lodge with ${preset.taxAuthorityLabel}.</span>
            <button onclick="window.print();" style="background: #0D9488; color: white; border: none; padding: 6px 16px; border-radius: 6px; font-weight: bold; cursor: pointer;">Print / Save PDF</button>
          </div>

          <div class="header">
            <div>
              <h1 class="company">${settings.companyName || settings.senderName || 'Operating Business Entity'}</h1>
              <div class="doc-title">${preset.nilReturnTitle}</div>
              <div style="font-size: 11px; color: #475569; margin-top: 4px;">
                Jurisdiction: <strong>${preset.name} (${preset.flag})</strong> | Authority Form: <strong>${preset.nilReturnAuthorityForm}</strong>
              </div>
            </div>
            <div class="meta">
              <div><strong>Prepared Date:</strong> ${dateStr}</div>
              <div style="margin-top: 4px;"><span class="badge-safe">0% TAX SAFE HARBOR</span></div>
            </div>
          </div>

          <div class="box-guidance">
            <strong>Statutory Exemption Notice:</strong> This business has generated <strong>${cur}${totalGrossRev.toFixed(2)}</strong> gross revenue during the fiscal period, remaining well below the statutory registration threshold of <strong>${preset.vatThresholdFormatted}</strong>. In accordance with ${preset.taxAuthorityLabel} rules, no output VAT/tax is collectible or payable.
          </div>

          <div class="kpi-grid">
            <div class="kpi-card">
              <div class="kpi-label">Gross Revenue Turnover</div>
              <div class="kpi-value" style="color: #0F766E;">${cur}${totalGrossRev.toFixed(2)}</div>
              <div class="kpi-sub">${thresholdPercentage.toFixed(1)}% of ${preset.vatThresholdFormatted}</div>
            </div>
            <div class="kpi-card">
              <div class="kpi-label">Deductible Startup Outflows</div>
              <div class="kpi-value" style="color: #B91C1C;">${cur}${totalGrossExp.toFixed(2)}</div>
              <div class="kpi-sub">${expenses.length} Tracked Startup Expenses</div>
            </div>
            <div class="kpi-card-zero">
              <div class="kpi-label">Net Tax Liability Payable</div>
              <div class="kpi-value">${cur}0.00</div>
              <div class="kpi-sub">Nil Liability (Safe Harbor Active)</div>
            </div>
          </div>

          <div class="section-header">Statutory eFiling Line Items Guide (${preset.nilReturnAuthorityForm})</div>
          <table class="filing-table">
            <thead>
              <tr>
                <th>Schedule Code / Field</th>
                <th>Statutory Description</th>
                <th style="text-align: right;">Value to Enter</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>TURNOVER / GROSS</strong></td>
                <td>Total gross receipts/sales for the fiscal period</td>
                <td style="text-align: right; font-weight: bold; color: #0F172A;">${cur}${totalGrossRev.toFixed(2)}</td>
              </tr>
              <tr>
                <td><strong>ALLOWABLE EXPENSES</strong></td>
                <td>Total deductible startup and operational disbursements</td>
                <td style="text-align: right; font-weight: bold; color: #0F172A;">${cur}${totalGrossExp.toFixed(2)}</td>
              </tr>
              <tr>
                <td><strong>TAXABLE OPERATING INCOME</strong></td>
                <td>Net profit after allowable deductibles</td>
                <td style="text-align: right; font-weight: bold; color: #0F172A;">${cur}${Math.max(0, netTaxableProfit).toFixed(2)}</td>
              </tr>
              <tr>
                <td><strong>NET TAX PAYABLE</strong></td>
                <td>Calculated liability after 0% base bracket & safe harbor</td>
                <td style="text-align: right; font-weight: 800; color: #059669; font-size: 14px;">${cur}0.00</td>
              </tr>
            </tbody>
          </table>

          <div class="loss-box">
            <strong>💡 Assessed Loss Carry-Forward Strategy:</strong>
            ${netTaxableProfit < 0 
              ? `You have an active operational loss of ${cur}${Math.abs(netTaxableProfit).toFixed(2)}. In ${preset.name}, this loss is carried forward to future profitable years to reduce or eliminate future taxes when your revenue scales above threshold.`
              : `Tracking all your software, hardware, and inventory costs in AzuraOS ensures you build an airtight deduction shield as your revenue approaches ${preset.vatThresholdFormatted}.`}
          </div>

          <footer style="margin-top: 40px; border-top: 1px solid #E2E8F0; padding-top: 12px; font-size: 10px; color: #94A3B8; text-align: center;">
            Prepared via AzuraOS Sovereign Build-Phase System • ${preset.nilReturnTitle}
          </footer>

          <script>
            window.onload = function() {
              setTimeout(function() { window.print(); }, 600);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
    setDownloading(null);
  };

  // Print / Save Full Tax PDF Summary
  const handlePrintTaxPDF = () => {
    setDownloading('tax_pdf');
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Could not open print window. Please allow popups for AzuraOS.');
      setDownloading(null);
      return;
    }

    const dateStr = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    const formattedFiscalEnd = fiscalEndDate.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });

    printWindow.document.write(`
      <html>
        <head>
          <title>Annual Fiscal Tax Summary Return - ${settings.companyName || 'AzuraOS'}</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #0F172A; line-height: 1.5; padding: 35px; max-width: 850px; margin: 0 auto; background: #FFF; }
            .no-print { background: #FFFBEB; border: 1px solid #FDE68A; padding: 12px 20px; border-radius: 8px; margin-bottom: 25px; display: flex; justify-content: space-between; align-items: center; font-size: 13px; color: #78350F; }
            .header { border-bottom: 3px solid #0F172A; padding-bottom: 15px; margin-bottom: 25px; display: flex; justify-content: space-between; align-items: flex-start; }
            .company { font-size: 22px; font-weight: 800; color: #0F172A; margin: 0; }
            .doc-title { font-size: 14px; font-weight: 700; text-transform: uppercase; color: #0D9488; letter-spacing: 0.05em; margin-top: 2px; }
            .meta { font-size: 11px; color: #64748B; text-align: right; }
            .tax-badge { background: #CCFBF1; color: #0F766E; border: 1px solid #99F6E4; padding: 2px 8px; border-radius: 4px; font-weight: 700; font-size: 10px; }
            
            .kpi-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin-bottom: 25px; }
            .kpi-card { background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 10px; padding: 15px; }
            .kpi-card-highlight { background: #134E4A; color: white; border-radius: 10px; padding: 15px; }
            .kpi-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #64748B; margin-bottom: 4px; }
            .kpi-card-highlight .kpi-label { color: #5EEAD4; }
            .kpi-value { font-size: 20px; font-weight: 800; font-family: monospace; }
            .kpi-sub { font-size: 10px; margin-top: 4px; color: #64748B; }
            .kpi-card-highlight .kpi-sub { color: #CCFBF1; }

            .section-header { font-size: 13px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.05em; color: #0F172A; border-bottom: 2px solid #E2E8F0; padding-bottom: 6px; margin: 25px 0 12px 0; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 15px; }
            th { background: #F8FAFC; text-align: left; padding: 8px 12px; font-weight: 700; color: #475569; border-bottom: 2px solid #E2E8F0; }
            td { padding: 8px 12px; border-bottom: 1px solid #F1F5F9; }
            
            @media print {
              body { padding: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="no-print">
            <span>💡 <strong>Accountant & Filing Ready:</strong> Choose <strong>"Save as PDF"</strong> in your browser to download this Annual Fiscal Tax Return.</span>
            <button onclick="window.print();" style="background: #0D9488; color: white; border: none; padding: 6px 16px; border-radius: 6px; font-weight: bold; cursor: pointer;">Print / Save PDF</button>
          </div>

          <div class="header">
            <div>
              <h1 class="company">${settings.companyName || settings.senderName || 'Your Business Name'}</h1>
              <div class="doc-title">Fiscal Year-End Tax Summary & Accountant Pack</div>
              <div style="font-size: 11px; color: #475569; margin-top: 4px;">
                Fiscal Concluding Date: <strong>${formattedFiscalEnd}</strong> | Authority: <strong>${taxLabel}</strong> | Tax Ref: <strong>${settings.taxNumber || 'Registered'}</strong>
              </div>
            </div>
            <div class="meta">
              <div><strong>Prepared Date:</strong> ${dateStr}</div>
              <div style="margin-top: 4px;"><span class="tax-badge">${rate}% TAX RATE (${cur})</span></div>
            </div>
          </div>

          <div class="kpi-grid">
            <div class="kpi-card">
              <div class="kpi-label">Gross Revenue Output Tax</div>
              <div class="kpi-value" style="color: #15803D;">${cur}${totalOutputVat.toFixed(2)}</div>
              <div class="kpi-sub">${rate}% on ${cur}${totalGrossRev.toFixed(2)} Gross Income</div>
            </div>
            <div class="kpi-card">
              <div class="kpi-label">Claimable Input Tax</div>
              <div class="kpi-value" style="color: #B91C1C;">${cur}${totalInputVat.toFixed(2)}</div>
              <div class="kpi-sub">${rate}% on ${cur}${totalGrossExp.toFixed(2)} Expenses</div>
            </div>
            <div class="kpi-card-highlight">
              <div class="kpi-label">Net Tax Position (${taxLabel})</div>
              <div class="kpi-value" style="color: #FDE047;">${cur}${netTaxPayable >= 0 ? netTaxPayable.toFixed(2) : `(${Math.abs(netTaxPayable).toFixed(2)} Refund)`}</div>
              <div class="kpi-sub">${netTaxPayable >= 0 ? 'Net Tax Payable to Authority' : 'Net Tax Refund Claimable'}</div>
            </div>
          </div>

          <div class="section-header">Summary Breakdown by Service Stream</div>
          <table>
            <thead>
              <tr>
                <th>Service Stream</th>
                <th>Gross Revenue</th>
                <th>Net (Excl. Tax)</th>
                <th>${rate}% Tax Portion</th>
              </tr>
            </thead>
            <tbody>
              ${streams.map(stream => {
                const inc = bookings.filter(b => b.type === stream).reduce((sum, b) => sum + b.price, 0);
                const net = inc / divisor;
                const vat = inc - net;
                return `
                  <tr>
                    <td style="font-weight: 600; text-transform: capitalize;">${stream}</td>
                    <td>${cur}${inc.toFixed(2)}</td>
                    <td>${cur}${net.toFixed(2)}</td>
                    <td style="font-weight: 700; color: #15803D;">${cur}${vat.toFixed(2)}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>

          <div class="section-header">Summary Breakdown by Expense Category</div>
          <table>
            <thead>
              <tr>
                <th>Category</th>
                <th>Gross Expenses</th>
                <th>Net (Excl. Tax)</th>
                <th>${rate}% Input Tax</th>
              </tr>
            </thead>
            <tbody>
              ${['travel', 'supplies', 'rent', 'other'].map(cat => {
                const exp = expenses.filter(e => e.category === cat).reduce((sum, e) => sum + e.amount, 0);
                const net = exp / divisor;
                const vat = exp - net;
                return `
                  <tr>
                    <td style="font-weight: 600; text-transform: capitalize;">${cat}</td>
                    <td>${cur}${exp.toFixed(2)}</td>
                    <td>${cur}${net.toFixed(2)}</td>
                    <td style="font-weight: 700; color: #B91C1C;">${cur}${vat.toFixed(2)}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>

          <footer style="margin-top: 40px; border-top: 1px solid #E2E8F0; padding-top: 12px; font-size: 10px; color: #94A3B8; text-align: center;">
            Generated via AzuraOS Sovereign Operating System • Ready for accountant filing review.
          </footer>

          <script>
            window.onload = function() {
              setTimeout(function() { window.print(); }, 600);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
    setDownloading(null);
  };

  // Export CSV Report
  const handleExportCSV = () => {
    setDownloading('tax_csv');
    const dateStr = new Date().toISOString().split('T')[0];
    let csv = `ANNUAL FISCAL YEAR-END TAX SCHEDULE\n`;
    csv += `Entity,"${(settings.companyName || settings.senderName || 'Your Business Name').replace(/"/g, '""')}"\n`;
    csv += `Jurisdiction,"${preset.name} (${preset.id})"\n`;
    csv += `Fiscal Year End Date,${fiscalEndDate.toISOString().split('T')[0]}\n`;
    csv += `Tax Reference,"${(settings.taxNumber || 'Unregistered').replace(/"/g, '""')}"\n`;
    csv += `Tax Authority,"${taxLabel.replace(/"/g, '""')}"\n`;
    csv += `Applied Rate,${rate}%\n`;
    csv += `Threshold Status,"${isUnderThreshold ? `UNDER THRESHOLD (${preset.vatThresholdFormatted}) - 0% SAFE HARBOR` : 'ABOVE THRESHOLD'}"\n\n`;

    csv += `EXECUTIVE TAX SUMMARY (${cur})\n`;
    csv += `Gross Revenue,${totalGrossRev.toFixed(2)},Output Tax (${rate}%),${totalOutputVat.toFixed(2)}\n`;
    csv += `Gross Expenses,${totalGrossExp.toFixed(2)},Input Tax (${rate}%),${totalInputVat.toFixed(2)}\n`;
    csv += `NET TAX PAYABLE / (REFUND),${netTaxPayable.toFixed(2)}\n`;
    csv += `NET TAXABLE OPERATING PROFIT / (LOSS),${netTaxableProfit.toFixed(2)}\n\n`;

    csv += `REVENUE BOOKINGS SCHEDULE\n`;
    csv += `Client,Stream,Date,Gross (${cur}),Net Excl. Tax (${cur}),Output Tax (${cur})\n`;
    bookings.forEach(b => {
      const gross = b.price;
      const net = gross / divisor;
      const vat = gross - net;
      csv += `"${(b.client_name || 'Client').replace(/"/g, '""')}","${b.type}","${b.date.split('T')[0]}",${gross.toFixed(2)},${net.toFixed(2)},${vat.toFixed(2)}\n`;
    });

    csv += `\nEXPENSES SCHEDULE\n`;
    csv += `Description,Category,Stream,Date,Gross (${cur}),Net Excl. Tax (${cur}),Input Tax (${cur})\n`;
    expenses.forEach(e => {
      const gross = e.amount;
      const net = gross / divisor;
      const vat = gross - net;
      csv += `"${(e.note || 'Expense').replace(/"/g, '""')}","${e.category}","${e.linked_stream}","${e.date.split('T')[0]}",${gross.toFixed(2)},${net.toFixed(2)},${vat.toFixed(2)}\n`;
    });

    setTimeout(() => {
      downloadFile(csv, `azuraos_annual_tax_pack_${fiscalEndDate.toISOString().split('T')[0]}.csv`, 'text/csv');
      setDownloading(null);
      showToast('Annual Tax Pack CSV downloaded successfully!', 'success');
    }, 400);
  };

  const formattedTargetDate = fiscalEndDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

  if (isDismissed) {
    return (
      <div className="flex items-center justify-between px-4 py-2.5 bg-[#121617] border border-[#20272A] rounded-xl text-xs text-gray-400">
        <div className="flex items-center gap-2">
          <Calendar size={14} className="text-[#14B8A6]" />
          <span>Fiscal Year-End Notice snoozed ({daysRemaining > 0 ? `${daysRemaining} days until Year-End` : 'Tax Season Active'}).</span>
        </div>
        <button
          onClick={() => {
            localStorage.removeItem(STORAGE_KEY);
            setIsDismissed(false);
            showToast('Fiscal Year-End Tax reminder restored.', 'info');
          }}
          className="text-xs text-[#14B8A6] hover:underline font-semibold cursor-pointer"
        >
          Show Notice
        </button>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#151C1D] via-[#121719] to-[#0E1213] border border-[#14B8A6]/30 shadow-xl p-5 sm:p-6 transition-all animate-fade-in space-y-5">
      {/* Ambient background glow */}
      <div className="absolute top-0 right-0 w-80 h-80 bg-[#14B8A6]/10 rounded-full blur-3xl pointer-events-none" />

      {/* Top Header Row */}
      <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="space-y-1.5 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#14B8A6]/20 border border-[#14B8A6]/40 text-[#14B8A6] text-[11px] font-bold tracking-wide uppercase font-mono">
              <Shield size={12} />
              {isUnderThreshold ? '0% Safe Harbor Active' : 'Standard Tax Schedule'}
            </span>

            <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#1F2628] text-gray-300 border border-[#2D373A] flex items-center gap-1 font-medium">
              <Clock size={11} className="text-amber-400" />
              Target: <strong className="text-white">{formattedTargetDate} ({daysRemaining}d)</strong>
            </span>

            <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#162224] text-teal-300 border border-teal-500/30 flex items-center gap-1 font-medium">
              <span>{preset.flag}</span>
              <span>{preset.name}</span>
            </span>

            <button
              type="button"
              onClick={() => setIsConfigOpen(!isConfigOpen)}
              className="text-[11px] text-gray-400 hover:text-[#14B8A6] transition flex items-center gap-1 underline underline-offset-2 cursor-pointer ml-1"
              title="Change your registered fiscal year-end month"
            >
              <Sliders size={11} /> Change Fiscal Month
            </button>
          </div>

          <h3 className="text-base sm:text-lg font-display font-bold text-white tracking-tight flex items-center gap-2">
            <span>{isUnderThreshold ? 'Build-Phase Zero-Tax Shield & Threshold Tracker' : 'Fiscal Year-End Tax & Accountant Submission'}</span>
          </h3>

          <p className="text-xs text-gray-400 max-w-2xl leading-relaxed">
            {isUnderThreshold 
              ? `You are in the build phase below the ${preset.vatThresholdFormatted} threshold. Your legal tax liability is ${cur}0.00. Use this tool to generate 1-click Nil returns for ${preset.taxAuthorityLabel} while banking deductible startup expenses.`
              : `As your business concludes its fiscal year on ${formattedTargetDate}, review output tax collected and input tax deductions for submission to ${taxLabel}.`}
          </p>

          {/* Quick Config Selector Dropdown */}
          {isConfigOpen && (
            <div className="mt-3 p-3 bg-[#0B0E0F] border border-[#263135] rounded-xl flex flex-col sm:flex-row sm:items-center gap-3 animate-fade-in text-xs">
              <span className="text-gray-300 font-semibold shrink-0">Your Fiscal Year Concludes In:</span>
              <select
                value={selectedMonth}
                onChange={(e) => handleUpdateFiscalMonth(parseInt(e.target.value, 10))}
                className="bg-[#141A1C] border border-[#2B383C] text-white text-xs rounded-lg px-3 py-1.5 focus:outline-none focus:border-[#14B8A6]"
              >
                {FISCAL_MONTH_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setIsConfigOpen(false)}
                className="text-[11px] text-gray-400 hover:text-white px-2 py-1 cursor-pointer"
              >
                Done
              </button>
            </div>
          )}
        </div>

        {/* View Mode Switcher */}
        <div className="flex items-center gap-2 bg-[#0C1011] p-1 rounded-xl border border-[#222B2E] self-start lg:self-center">
          <button
            type="button"
            onClick={() => setActiveTab('zero_tax')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
              activeTab === 'zero_tax' ? 'bg-[#14B8A6] text-black shadow-md' : 'text-gray-400 hover:text-white'
            }`}
          >
            <Shield size={13} />
            <span>Zero-Tax Shield</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('standard')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
              activeTab === 'standard' ? 'bg-[#14B8A6] text-black shadow-md' : 'text-gray-400 hover:text-white'
            }`}
          >
            <FileText size={13} />
            <span>VAT / Tax Schedule</span>
          </button>
          <button
            type="button"
            onClick={() => handleDismiss(14)}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-200 hover:bg-[#1A2224] transition cursor-pointer ml-1"
            title="Snooze reminder for 14 days"
          >
            <X size={15} />
          </button>
        </div>
      </div>

      {/* Mode 1: Zero-Tax & Build-Phase Safe Harbor Shield */}
      {activeTab === 'zero_tax' ? (
        <div className="space-y-4 pt-2 border-t border-[#1F2729]">
          {/* Milestone Progress Bar */}
          <div className="p-4 bg-[#0A0D0E] border border-[#20292B] rounded-xl space-y-2">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 text-xs">
              <div className="flex items-center gap-2">
                <TrendingUp size={14} className="text-[#14B8A6]" />
                <span className="font-bold text-white">{preset.thresholdMilestoneLabel}</span>
              </div>
              <div className="text-gray-400 text-[11px] font-mono">
                <span className="text-[#14B8A6] font-bold">{cur}{totalGrossRev.toFixed(0)}</span> of <span className="text-white font-bold">{cur}{vatThreshold.toLocaleString()}</span> ({thresholdPercentage.toFixed(1)}%)
              </div>
            </div>

            {/* Visual Gauge Bar */}
            <div className="w-full bg-[#171E20] h-2.5 rounded-full overflow-hidden p-0.5 border border-[#28363A]">
              <div 
                className="bg-gradient-to-r from-emerald-500 via-[#14B8A6] to-teal-400 h-full rounded-full transition-all duration-500 shadow-sm"
                style={{ width: `${Math.max(4, thresholdPercentage)}%` }}
              />
            </div>

            <div className="flex items-center justify-between text-[10px] text-gray-500">
              <span>{cur}0 (Day 0)</span>
              <span>Safe Harbor: {cur}{remainingToThreshold.toLocaleString()} headroom remaining</span>
              <span>{preset.vatThresholdFormatted}</span>
            </div>
          </div>

          {/* 3 Value Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="p-3.5 bg-[#0C1011] border border-[#222B2E] rounded-xl space-y-1">
              <div className="text-[10px] uppercase font-bold text-gray-400">Current Tax Liability</div>
              <div className="text-xl font-bold font-mono text-emerald-400">{cur}0.00</div>
              <div className="text-[10px] text-emerald-300/80 font-medium">100% Tax-Free Safe Harbor Active</div>
            </div>

            <div className="p-3.5 bg-[#0C1011] border border-[#222B2E] rounded-xl space-y-1">
              <div className="text-[10px] uppercase font-bold text-gray-400">Assessed Loss / Startup Shelter</div>
              <div className="text-xl font-bold font-mono text-amber-400">{cur}{totalGrossExp.toFixed(2)}</div>
              <div className="text-[10px] text-gray-400">{expenses.length} expenses ready to shelter future profits</div>
            </div>

            <div className="p-3.5 bg-[#0C1011] border border-[#222B2E] rounded-xl space-y-1">
              <div className="text-[10px] uppercase font-bold text-gray-400">Authority Guidance</div>
              <div className="text-xs font-bold text-white">{preset.taxAuthorityLabel.split('(')[0]}</div>
              <div className="text-[10px] text-gray-400 truncate">{preset.nilReturnAuthorityForm}</div>
            </div>
          </div>

          {/* Statutory Advice & Action Footer */}
          <div className="p-3.5 bg-[#0C1011] border border-[#14B8A6]/20 rounded-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs">
            <div className="text-gray-300 leading-relaxed text-[11.5px] max-w-xl">
              <strong>Why log expenses now?</strong> {preset.buildPhaseAdvice}
            </div>

            <div className="flex items-center gap-2 w-full md:w-auto shrink-0">
              <button
                type="button"
                onClick={handlePrintNilReturnPDF}
                disabled={downloading === 'nil_pdf'}
                className="flex-1 md:flex-initial px-4 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-xs transition-all shadow-md shadow-emerald-500/20 flex items-center justify-center gap-2 cursor-pointer active:scale-95 disabled:opacity-50"
                title="Generate pre-filled Zero Liability return summary for eFiling"
              >
                <FileCheck size={15} />
                <span>1-Click Nil Return Pack (PDF)</span>
              </button>

              <button
                type="button"
                onClick={handleExportCSV}
                disabled={downloading === 'tax_csv'}
                className="px-3.5 py-2.5 rounded-xl bg-[#1A2224] hover:bg-[#253034] text-gray-200 border border-[#2C393D] font-semibold text-xs transition flex items-center justify-center gap-1.5 cursor-pointer"
              >
                <FileSpreadsheet size={14} className="text-[#14B8A6]" />
                <span>CSV</span>
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* Mode 2: Standard Fiscal Year-End Tax Schedule */
        <div className="space-y-4 pt-2 border-t border-[#1F2729]">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="p-3.5 bg-[#0C1011] border border-[#222B2E] rounded-xl space-y-1">
              <div className="text-[10px] uppercase font-bold text-gray-400">Gross Billed Output VAT ({rate}%)</div>
              <div className="text-lg font-bold font-mono text-emerald-400">+{cur}{totalOutputVat.toFixed(2)}</div>
              <div className="text-[10px] text-gray-500">From {cur}{totalGrossRev.toFixed(2)} gross sales</div>
            </div>

            <div className="p-3.5 bg-[#0C1011] border border-[#222B2E] rounded-xl space-y-1">
              <div className="text-[10px] uppercase font-bold text-gray-400">Claimable Input Tax Deductions</div>
              <div className="text-lg font-bold font-mono text-rose-400">-{cur}{totalInputVat.toFixed(2)}</div>
              <div className="text-[10px] text-gray-500">From {cur}{totalGrossExp.toFixed(2)} expenses</div>
            </div>

            <div className="p-3.5 bg-[#134E4A]/30 border border-[#14B8A6]/40 rounded-xl space-y-1">
              <div className="text-[10px] uppercase font-bold text-teal-300">Net Estimated Tax Position</div>
              <div className="text-lg font-bold font-mono text-amber-300">
                {cur}{netTaxPayable >= 0 ? netTaxPayable.toFixed(2) : `(${Math.abs(netTaxPayable).toFixed(2)} Refund)`}
              </div>
              <div className="text-[10px] text-teal-200/80">{netTaxPayable >= 0 ? `Payable to ${taxLabel}` : 'Claimable as Refund'}</div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={handlePrintTaxPDF}
              disabled={downloading === 'tax_pdf'}
              className="px-4 py-2.5 rounded-xl bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-bold text-xs transition-all shadow-md shadow-[#14B8A6]/20 flex items-center justify-center gap-1.5 cursor-pointer active:scale-95 disabled:opacity-50"
            >
              <FileText size={14} />
              <span>Full Tax Schedule (PDF)</span>
            </button>

            <button
              type="button"
              onClick={handleExportCSV}
              disabled={downloading === 'tax_csv'}
              className="px-3.5 py-2.5 rounded-xl bg-[#1A2224] hover:bg-[#253034] text-gray-200 border border-[#2C393D] font-semibold text-xs transition flex items-center justify-center gap-1.5 cursor-pointer"
            >
              <FileSpreadsheet size={14} className="text-[#14B8A6]" />
              <span>Export CSV</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export const FiscalYearTaxReminder = React.memo(FiscalYearTaxReminderComponent);

